import { NavBar } from 'components/NavBar/NavBar'
import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { RootState } from 'store'
import {
    Flex,
    MainHeadline,
    PageFooter,
    PageWrapper,
} from 'styling/GlobalStyles'
import { OtherText } from 'styling/Typography'
import { tokens } from 'styling/tokens'
import { Pagination } from 'components/Pagination/Pagination'
import { formatDateForDisplay, formatNumbersForDisplay, getParams, handlePagination } from 'utils/helpers'
import { NoItems } from 'components/NoItems-fallback/NoItems'
import useQueryPagination from 'hooks/useQueryPagination'
import { clearFuelLosses, getFuelLosses } from 'features/fuelManagement/fuelManagementSlice'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { AlarmBanner, AlarmContent, AlarmDot, AlarmText } from 'pages/Operations/Safety/Safety.styled'
import { DemoBadge } from 'pages/Operations/Shared/OpsSharedComponents'
import { IStatusPill } from 'types/ui/statusPill.interface'
import { IFuelOpsLossRow, ILossOverrideMap } from 'types/fuelOps.interface'
import { IGetAllFuelLossesSingleResponse } from 'types/fuelManagement.interface'
import {
    criticalLossBannerText,
    findOpenCriticalLoss,
    formatGhs,
    fuelLossCostGhs,
    FUEL_DEMO_LOSSES,
    FUEL_LOSS_CLOSURE_CAPTION,
    FUEL_RESIDUAL_EXPLAINER,
    FUEL_WRITE_OFF_APPROVAL_THRESHOLD_GHS,
} from '../fuelOpsDemoData'
import { LossCellEm, LossExplainerWrap, LossInvestigationCell } from './FuelLosses.styled'
import { CASE_STATUS_META, LossCaseModal, OUTCOME_META } from './LossCaseModal'

const CRITICAL_LITERS_THRESHOLD = 1000

const STATUS_TONE: { [k in IFuelOpsLossRow['status']]: IStatusPill['status'] } = {
    'Under investigation': 'danger',
    'Explained': 'success',
    'Written off — signed': 'neutral',
}

const mapRealLoss = (item: IGetAllFuelLossesSingleResponse): IFuelOpsLossRow => {
    const severity = item.amount >= CRITICAL_LITERS_THRESHOLD ? 'critical' : 'warning'
    return {
        id: item.id,
        dateLabel: formatDateForDisplay(item.date),
        liters: item.amount,
        tank: item.tank?.name || '/',
        classification: 'Residual · Operational · Batch Close',
        severity,
        owner: severity === 'critical' ? 'Unassigned' : '—',
        status: severity === 'critical' ? 'Under investigation' : 'Explained',
        caseStatus: 'OPEN',
        outcome: null,
    }
}

const columns: IHonestTableColumn<IFuelOpsLossRow>[] = [
    { id: 'date', header: 'DATE', cell: (item) => item.dateLabel },
    {
        id: 'severity',
        header: 'SEVERITY',
        cell: (item) => (
            <StatusPill status={item.severity === 'critical' ? 'danger' : 'warning'} dot={item.severity === 'critical'}>
                {item.severity === 'critical' ? 'Critical' : 'Warning'}
            </StatusPill>
        ),
    },
    {
        id: 'amount',
        header: 'AMOUNT',
        cell: (item) => <LossCellEm>{`${formatNumbersForDisplay(item.liters)} L`}</LossCellEm>,
    },
    {
        id: 'cost',
        header: 'COST',
        cell: (item) => (item.outcome === 'RECOVERED'
            ? <StatusPill status='success'>Recovered</StatusPill>
            : (
                <StatusPill status={item.severity === 'critical' ? 'danger' : 'neutral'}>
                    {formatGhs(fuelLossCostGhs(item.liters))}
                </StatusPill>
            )),
    },
    { id: 'tank', header: 'TANK', cell: (item) => item.tank },
    { id: 'classification', header: 'CLASSIFICATION', cell: (item) => item.classification },
    { id: 'owner', header: 'OWNER', cell: (item) => item.owner },
    {
        id: 'status',
        header: 'STATUS',
        cell: (item) => {
            const meta = CASE_STATUS_META[item.caseStatus || 'OPEN']
            return (
                <StatusPill status={meta.tone} dot={item.caseStatus === 'PENDING_APPROVAL'}>
                    {meta.label}
                </StatusPill>
            )
        },
    },
    {
        id: 'outcome',
        header: 'OUTCOME',
        cell: (item) => (item.outcome
            ? <StatusPill status={OUTCOME_META[item.outcome].tone}>{OUTCOME_META[item.outcome].label}</StatusPill>
            : <OtherText grey>—</OtherText>),
    },
    {
        id: 'investigation',
        header: 'INVESTIGATION',
        minWidth: '18rem',
        cell: (item) => (
            <LossInvestigationCell>
                <StatusPill status={STATUS_TONE[item.status]} dot={item.status === 'Under investigation' && item.severity === 'critical'}>
                    {item.status}
                </StatusPill>
                {item.note && <OtherText color={tokens.text.muted}>{item.note}</OtherText>}
            </LossInvestigationCell>
        ),
    },
]

export const FuelLosses = () => {
    const [params, setParams] = useState<any>(getParams({ location }))
    const { fuelLosses } = useSelector((store: RootState) => store.fuelManagement)
    const [overrides, setOverrides] = useState<ILossOverrideMap>({})
    const [selectedLossId, setSelectedLossId] = useState<number | null>(null)

    useQueryPagination({ fetchFunctions: [getFuelLosses], cleanUpFunctions: [clearFuelLosses], params })

    const usingDemoLosses = !fuelLosses?.data?.length && FUEL_DEMO_LOSSES.length > 0

    const rows: IFuelOpsLossRow[] = useMemo(() => {
        const base = fuelLosses?.data?.length ? fuelLosses.data.map(mapRealLoss) : FUEL_DEMO_LOSSES
        return base.map((row) => (overrides[row.id] ? { ...row, ...overrides[row.id] } : row))
    }, [fuelLosses, overrides])

    const criticalLoss = findOpenCriticalLoss(rows)
    const selectedLoss = rows.find((row) => row.id === selectedLossId) || null

    const handleApproveWriteOff = (loss: IFuelOpsLossRow) => {
        setOverrides((prev) => ({
            ...prev,
            [loss.id]: {
                caseStatus: 'CLOSED',
                outcome: 'WRITTEN_OFF',
                closedAt: 'Today',
                status: 'Written off — signed',
                approval: {
                    ...(loss.approval || {
                        requiredAboveGhs: FUEL_WRITE_OFF_APPROVAL_THRESHOLD_GHS,
                        approverRole: 'GM',
                    }),
                    approvedBy: 'GM',
                    approvedAt: 'Today',
                },
            },
        }))
        toast.success(`Write-off ${formatGhs(fuelLossCostGhs(loss.liters))} approved by GM — case closed`)
        setSelectedLossId(null)
    }

    const handleRequestInvestigation = (loss: IFuelOpsLossRow) => {
        setOverrides((prev) => ({
            ...prev,
            [loss.id]: { caseStatus: 'OPEN', outcome: null },
        }))
        toast.info(`Case returned for investigation — owner ${loss.owner} notified`)
        setSelectedLossId(null)
    }

    return (
        <>
            <PageWrapper>
                <NavBar />
                <Flex between mb='16' >
                    <Flex column gap='0.25rem'>
                        <Flex center gap='0.75rem'>
                            <MainHeadline>Fuel Losses</MainHeadline>
                            {usingDemoLosses && <DemoBadge />}
                        </Flex>
                        <OtherText grey>Residual variances by severity, cost, closure status and outcome</OtherText>
                    </Flex>
                </Flex>
                {criticalLoss && (
                    <AlarmBanner style={{ marginTop: 0, marginBottom: '1rem' }}>
                        <AlarmContent>
                            <AlarmDot />
                            <AlarmText>{criticalLossBannerText(criticalLoss)}</AlarmText>
                        </AlarmContent>
                    </AlarmBanner>
                )}
                {!rows.length ? (
                    <NoItems title='Fuel Losses' />
                ) : (
                    <>
                        <HonestTable
                            columns={columns}
                            data={rows}
                            keyField='id'
                            onClick={(item) => setSelectedLossId(item.id)}
                        />
                        <LossExplainerWrap>
                            <OtherText color={tokens.text.muted}>{FUEL_LOSS_CLOSURE_CAPTION}</OtherText>
                            <OtherText color={tokens.text.muted}>{FUEL_RESIDUAL_EXPLAINER}</OtherText>
                        </LossExplainerWrap>
                    </>
                )}
                {selectedLoss && (
                    <LossCaseModal
                        loss={selectedLoss}
                        closeModal={() => setSelectedLossId(null)}
                        onApproveWriteOff={handleApproveWriteOff}
                        onRequestInvestigation={handleRequestInvestigation}
                    />
                )}
            </PageWrapper>
            <PageFooter>
                <Pagination
                    setPage={(page) => handlePagination(page, setParams)}
                    pageCount={Math.ceil(fuelLosses.totalPages)}
                    forcePage={(params.page ? params.page : 1) - 1}
                />
            </PageFooter>
        </>
    )
}
